import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient, ApiClientError } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { useCdpAuth } from "../lib/coinbase/useAuth";
import { trackSignUp } from "../lib/xPixel";

/**
 * The authoritative server-side principal (role, suspension-relevant status derived
 * server-side, canonical wallet address) for the signed-in CDP user. Never trust the
 * client-side wallet address for authorization — this is what backs role/suspension gates.
 */
export function useAuthPrincipal() {
  const cdp = useCdpAuth();

  const query = useQuery({
    queryKey: queryKeys.me,
    queryFn: ({ signal }) => apiClient.getMe(signal),
    enabled: cdp.isInitialized && cdp.isSignedIn,
    retry: (count, error) => {
      // 403 (FORBIDDEN) is a real authorization decision — never retry-worthy. 401 is
      // deliberately still retried a couple times: see queryClient.ts's shouldRetry docblock —
      // this call is just as exposed to the transient post-wallet-action token window as any
      // other authenticated query. A genuinely signed-out session still ends up failed either way.
      if (error instanceof ApiClientError && error.status === 403) {
        return false;
      }
      return count < 2;
    },
    staleTime: 60_000,
  });

  // isNewUser is true only on the exact backend request that created this account (see
  // services/identity.ts) — trackSignUp itself also dedupes via localStorage, so this fires the
  // conversion event once, ever, per browser, regardless of how many times this query re-runs.
  useEffect(() => {
    if (query.data?.isNewUser) trackSignUp(query.data.emailHash);
  }, [query.data?.isNewUser, query.data?.emailHash]);

  return {
    ...cdp,
    // Never surface a cached principal while signed out: the query keeps its last data after
    // `enabled` goes false, and a leftover principal makes a signed-out visitor look like the
    // previous user (e.g. "this is your own listing").
    principal: cdp.isSignedIn ? (query.data ?? null) : null,
    isLoadingPrincipal: cdp.isSignedIn && query.isLoading,
    principalError: query.error,
  };
}
